<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <h1>Cancel Reservation</h1>

      <!-- Reservation Summary Section -->
      <div class="summary-section">
        <h2>Reservation Summary</h2>
        <div class="tour-info">
          <img :src="listing.bannerUrl" alt="Tour" class="tour-thumbnail" />
          <div class="tour-details">
            <h3>{{ listing.title }}</h3>
            <p class="location">{{ listing.location }}</p>
          </div>
        </div>
        <div class="divider"></div>
        <div class="summary-row">
          <span>PILOT'S TIME</span>
          <span class="summary-value">
            <div class="simple-date">
              {{ formatSimpleDate(reservation.eventSchedule?.listing?.date) }}
            </div>
            <div class="simple-time-wrapper">
              <div class="simple-time">
                {{ reservation.eventSchedule?.listing?.time }}
              </div>
              <div class="simple-timezone">
                {{ reservation.eventSchedule?.listing?.timezone }}
              </div>
            </div>
          </span>
        </div>
        <div class="summary-row">
          <span>GUEST'S TIME</span>
          <span class="summary-value">
            <div class="simple-date">
              {{ formatSimpleDate(reservation.eventSchedule?.user?.date) }}
            </div>
            <div class="simple-time-wrapper">
              <div class="simple-time">
                {{ reservation.eventSchedule?.user?.time }}
              </div>
              <div class="simple-timezone">
                {{ reservation.eventSchedule?.user?.timezone }}
              </div>
            </div>
          </span>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Warning Section -->
      <div class="warning-section">
        <h2>Important Information</h2>
        <p>By cancelling this reservation:</p>
        <ul>
          <li>
            {{
              reservation.guestCount === 1
                ? "The guest"
                : `All ${reservation.guestCount} guests`
            }}
            will be notified immediately
          </li>
          <li>The time slot will be made available again</li>
          <li>Credits will be refunded to all affected guests</li>
          <li>This action cannot be undone</li>
        </ul>
      </div>

      <div class="note">
        Are you sure you want to cancel
        {{
          reservation.guestCount === 1
            ? "this reservation"
            : `all ${reservation.guestCount} reservations for this time slot`
        }}?
      </div>

      <div class="button-group">
        <button
          class="cancel-btn"
          @click="handleCancelReservation"
          :disabled="isLoading"
        >
          <template v-if="isLoading">
            <div class="spinner"></div>
            <span style="margin-left: 8px">Cancelling...</span>
          </template>
          <span v-else>Yes, Cancel Reservation</span>
        </button>
        <button class="close-btn" @click="$emit('close')" :disabled="isLoading">
          No, Keep It
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { db } from "@/plugins/firebase";
import { useToast } from "vue-toastification";
import { creditService } from "@/services/creditService";

export default {
  name: "CancelReservationModal",
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    listing: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      isLoading: false,
    };
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatEventDate(dateString, part) {
      if (!dateString) return "";

      // Parse date like "March 13, 2025"
      const parts = dateString.split(/[\s,]+/); // Split by spaces and commas

      if (part === "month" && parts.length > 0) {
        // Return the month in uppercase abbreviation (e.g., "MAR")
        return parts[0].substring(0, 3).toUpperCase();
      } else if (part === "day" && parts.length > 1) {
        // Return the day as a number
        return parseInt(parts[1]);
      }
      return "";
    },
    formatSimpleDate(dateString) {
      if (!dateString) return "";
      // dateString is in format "March 11, 2025"
      return dateString; // Already in the desired format
    },
    async handleCancelReservation() {
      this.isLoading = true;

      try {
        // Get the slotId from reservation to identify which slot needs to be updated
        const slotId = this.reservation.slotId;

        if (!slotId) {
          console.error("No slotId found in reservation:", this.reservation);
          throw new Error("Could not find slot ID in reservation data");
        }

        console.log("Looking for slot with ID:", slotId);

        // Find the matching slot in the listing's eventSchedule array
        if (
          !this.listing.eventSchedule ||
          !Array.isArray(this.listing.eventSchedule)
        ) {
          console.error(
            "No eventSchedule array found in listing:",
            this.listing
          );
          throw new Error("Listing does not have eventSchedule data");
        }

        // Find the matching slot index in the eventSchedule array
        const slotIndex = this.listing.eventSchedule.findIndex(
          (slot) => slot.slotId === slotId
        );

        if (slotIndex === -1) {
          console.error(
            "No matching slot found in eventSchedule. Available slots:",
            this.listing.eventSchedule.map((s) => s.slotId)
          );
          throw new Error("Could not find matching slot in listing");
        }

        // Create a local deep copy of the eventSchedule array
        const updatedEventSchedule = JSON.parse(
          JSON.stringify(this.listing.eventSchedule)
        );

        // Update the slot availability and reset guest count in our local copy
        updatedEventSchedule[slotIndex].isAvailable = true;
        updatedEventSchedule[slotIndex].currentGuestCount = 0;

        console.log(
          "Updated slot availability:",
          updatedEventSchedule[slotIndex]
        );

        // Update all consolidated reservations status
        const updatePromises = this.reservation.consolidatedReservations.map(
          async (reservationData) => {
            const reservationRef = doc(db, "reservations", reservationData.id);
            return updateDoc(reservationRef, {
              isCancelled: true,
              isConfirmed: false,
              status: "cancelled",
              updatedAt: new Date(),
            });
          }
        );

        // Wait for all reservation updates to complete
        await Promise.all(updatePromises);

        console.log(
          "Successfully updated all reservation statuses, now updating listing"
        );

        // Update the listing with our modified local copy of eventSchedule
        const listingRef = doc(db, "listings", this.listing.id);
        await updateDoc(
          listingRef,
          {
            eventSchedule: updatedEventSchedule,
          },
          { merge: true }
        );

        console.log("Successfully updated listing, now refunding credits");

        // Refund credits for all consolidated reservations
        const refundPromises = this.reservation.consolidatedReservations.map(
          async (reservationData) => {
            // Get credit history for each user
            const creditQuery = query(
              collection(db, "credits"),
              where("userId", "==", reservationData.createdBy),
              orderBy("createdAt", "desc")
            );
            const creditSnapshot = await getDocs(creditQuery);
            const creditHistory = creditSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            // Refund the credit for this user
            return creditService.refundCredit(
              reservationData.createdBy,
              creditHistory,
              reservationData.id
            );
          }
        );

        // Wait for all refunds to complete
        await Promise.all(refundPromises);

        console.log("Cancellation process completed successfully");

        // Add 2 second delay before closing
        await new Promise((resolve) => setTimeout(resolve, 2000));

        this.toast.success(
          `${this.reservation.guestCount} reservation(s) cancelled successfully`
        );

        this.$emit("close");
      } catch (error) {
        console.error("Error in cancellation process:", error);
        console.error("Full error context:", {
          reservation: this.reservation,
          listing: this.listing,
          slotId:
            this.reservation.slotId ||
            (this.reservation.eventSchedule &&
              this.reservation.eventSchedule.slotId),
        });
        this.toast.error("Failed to cancel reservation. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

h1 {
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 1.5rem;
  color: #111827;
}

h2 {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 1rem;
  color: #374151;
}

.summary-section {
  background-color: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.tour-info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.tour-thumbnail {
  width: 100px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
}

.tour-details {
  flex: 1;
  text-align: left;
}

.tour-details h3 {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.25rem;
}

.location {
  font-size: 0.875rem;
  color: #6b7280;
}

.summary-row {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #6b7280;
  align-items: flex-start;
}

.summary-value {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  margin-top: 0.2rem;
  gap: 1rem;
  width: 100%;
}

.summary-row.total {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  font-weight: 500;
  color: #111827;
}

.divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 1rem 0;
}

.warning-section {
  margin-bottom: 1.5rem;
}

.warning-section ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.warning-section li {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
}

.warning-section li::before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #ef4444;
}

.note {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #fee2e2;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
}

.button-group {
  display: flex;
  gap: 1rem;
  flex-direction: column;
}

.cancel-btn,
.close-btn {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  height: 48px; /* Fixed height for consistency */
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn {
  background-color: #ef4444;
  color: white;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #dc2626;
}

.close-btn {
  background-color: #f3f4f6;
  color: #374151;
}

.close-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.cancel-btn:disabled,
.close-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .modal-content {
    padding: 1.5rem;
    width: 95%;
  }

  h1 {
    font-size: 20px;
  }

  h2 {
    font-size: 16px;
  }
}

.time-display {
  display: flex;
  align-items: center;
  gap: 12px;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.calendar-month {
  color: #111827;
  font-size: 0.6rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.calendar-day {
  font-size: 1.8rem;
  font-weight: 300;
  color: #111827;
  line-height: 1;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.timezone-value {
  font-size: 0.75rem;
  color: #6b7280;
}

.simple-date {
  font-size: 0.9rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
}

.simple-time-wrapper {
  display: flex;
  flex-direction: column;
  margin-left: auto;
}

.simple-time {
  font-size: 0.9rem;
  font-weight: 500;
  color: #111827;
  text-align: right;
}

.simple-timezone {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
}
</style>
